import { useEffect, useRef, useState } from "react";
import secuIssue from "../Login/security_issue.json"
import bcrypt from "bcryptjs";
import { useDispatch, useSelector } from "react-redux";
import { login, setSubmitted, setShowPlane } from "../../store/reducers/progressionReducer";
import { useLocation } from 'react-router-dom';
import mails from "./emails.json"
import styles from "./Login.module.css"
import controlsStyle from "../Controls/Controls.module.css"
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap"
import TallPanel from "../SvgComponents/TallPanel";
import { v4 as uuidv4 } from "uuid";
import { logEvent, setUserEmail, trackSvcEvent } from "../../analytics";

export default function Login() {
    const [mdp, setMdp] = useState("");
    const [mail, setMail] = useState("");
    const [error, setError] = useState("");
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [showUpdatePassword, setShowUpdatePassword] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [resetSuccess, setResetSuccess] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const token = useSelector((state) => state.progression.token);
    const dispatch = useDispatch();
    const tlRef = useRef();
    const location = useLocation();

    useEffect(() => {
        //Get MDP Hashed
        /* const salt = bcrypt.hash("mdpHashed",secuIssue.salt).then((v) => {
           console.log(v)
         });*/
    }, [])

    useEffect(() => {
        // Petit délai pour s'assurer que tous les éléments sont bien montés
        const initTimer = setTimeout(() => {
            setIsInitialized(true);
        }, 300);

        return () => clearTimeout(initTimer);
    }, []);

    useGSAP(() => {
        // Ne pas exécuter l'animation si le composant n'est pas encore initialisé
        if (!isInitialized) return;

        tlRef.current?.kill();
        if (token) {
            tlRef.current = gsap.timeline().to(`.${styles.formContainer}`, {
                opacity: 0,
                pointerEvents: "none",
                duration: 1,
                ease: "power2.out",
            });
        }
        else {
            tlRef.current = gsap.timeline().to(`.${styles.formContainer}`, {
                opacity: 1,
                pointerEvents: "auto",
                duration: 0.3, // Augmenter légèrement pour une transition plus douce
                delay: 0.2,    // Léger délai pour s'assurer que tout est prêt
                ease: "power1.out", // Animation plus douce
                onStart: () => {
                    // S'assurer que le formulaire est visible et interactive
                    document.querySelector(`.${styles.formContainer}`).style.pointerEvents = "auto";
                }
            });
        }
    }, { dependencies: [token, isInitialized] }); // Ajouter isInitialized comme dépendance

    function handleChange(event) {
        if (event.target.dataset.entry === "mdp") {
            setMdp(event.target.value);
        }
        else if (event.target.dataset.entry === "mail") {
            setMail(event.target.value);
        }
        else if (event.target.dataset.entry === "newPassword") {
            setNewPassword(event.target.value);
        }
        else if (event.target.dataset.entry === "confirmPassword") {
            setConfirmPassword(event.target.value);
        }
    }

    function handleSubmit(event) {
        event.preventDefault();

        // Validation basique côté client
        if (!mail || !mdp) {
            setError("Veuillez remplir tous les champs");
            return;
        }

        // Désactiver temporairement le bouton pour éviter les soumissions multiples
        const submitButton = event.target.querySelector('input[type="submit"]');
        if (submitButton) submitButton.disabled = true;

        // Afficher un message de chargement
        setError("Connexion en cours...");

        // Ajouter un timeout pour éviter les requêtes bloquées indéfiniment
        const fetchTimeout = setTimeout(() => {
            setError("La connexion prend plus de temps que prévu. Veuillez réessayer.");
            if (submitButton) submitButton.disabled = false;
        }, 15000);

        // Envoi du formulaire avec une meilleure gestion des erreurs
        fetch("https://saviol-back-main.onrender.com/loginClinic", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: mail, password: mdp })
        })
            .then(response => {
                clearTimeout(fetchTimeout);

                if (!response.ok) {
                    throw new Error(`Erreur: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                if (data.error) {
                    setError(data.error);
                    throw new Error(data.error);
                }

                // Réinitialiser l'erreur en cas de succès
                setError("");
                dispatch(login({ token: data.token, email: mail }));
                dispatch(setSubmitted(true));

                // Définir l'email de l'utilisateur pour le filtrage analytics
                setUserEmail(mail);

                // Enregistrer l'événement de connexion réussie
                logEvent("User", "login", "success");
                trackSvcEvent("login", 1);

                setTimeout(() => {
                    dispatch(setShowPlane(true));
                }, 3600);
            })
            .catch(error => {
                console.error("Erreur lors de la connexion:", error);
                setError(error.message || "Une erreur est survenue. Veuillez réessayer.");

                // Enregistrer l'événement d'échec de connexion
                logEvent("User", "login", "failed");
                trackSvcEvent("login", 0);
            })
            .finally(() => {
                // Réactiver le bouton
                if (submitButton) submitButton.disabled = false;
            });
    }

    // Fonction pour gérer la demande de réinitialisation de mot de passe
    function handleResetPassword(e) {
        e.preventDefault();

        if (!mail) {
            setError("Veuillez entrer votre adresse e-mail");
            return;
        }

        // Enregistrer l'événement de demande de réinitialisation
        logEvent("User", "password_reset_request", mail);

        fetch("https://saviol-back-main.onrender.com/resetPassword", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: mail })
        })
            .then(response => response.json())
            .then(data => {
                if (data.error) {
                    setError(data.error);
                    logEvent("User", "password_reset_failed", mail);
                    throw new Error(data.error);
                }
                setResetSuccess(true);
                logEvent("User", "password_reset_email_sent", mail);
            })
            .catch(error => {
                console.error("error reset password", error);
            });
    }

    // Fonction pour mettre à jour le mot de passe
    function handleUpdatePassword(e) {
        e.preventDefault();
        console.log("Tentative de mise à jour du mot de passe");
        console.log("URL complète:", window.location.href);

        if (newPassword !== confirmPassword) {
            setError("Les mots de passe ne correspondent pas");
            return;
        }

        // Extraire le token directement de l'URL complète
        const tokenMatch = window.location.href.match(/token=([^&]+)/);
        const token = tokenMatch ? tokenMatch[1] : null;

        console.log("Token extrait:", token ? "présent" : "manquant");

        if (!token) {
            setError("Token de réinitialisation invalide");
            return;
        }

        console.log("Envoi de la requête avec le token");
        fetch("https://saviol-back-main.onrender.com/updatePassword", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                token: token,
                password: newPassword
            })
        })
            .then(response => {
                console.log("Réponse reçue:", response.status);
                return response.json();
            })
            .then(data => {
                console.log("Données reçues:", data);
                if (data.error) {
                    setError(data.error);
                    logEvent("User", "password_update_failed", data.error);
                    throw new Error(data.error);
                }
                setShowUpdatePassword(false);
                setResetSuccess(true);
                setError("");
                logEvent("User", "password_updated", "success");
            })
            .catch(error => {
                console.error("Erreur lors de la mise à jour du mot de passe:", error);
                setError(error.message || "Une erreur est survenue lors de la mise à jour du mot de passe");
                logEvent("User", "password_update_error", error.message || "unknown_error");
            });
    }

    // Vérifier si l'URL contient un token de réinitialisation
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const resetToken = searchParams.get('token');
        const path = location.pathname;
        const hash = location.hash;

        // Vérifier si nous sommes sur la route de mise à jour du mot de passe
        // avec HashRouter, le format sera /#/update-password
        if (resetToken && (path === '/update-password' || hash.includes('/update-password'))) {
            setShowUpdatePassword(true);
        }
    }, [location]);

    return (
        <div className={`${styles.formContainer} ${isInitialized ? styles.ready : ''}`}>
            {!showResetPassword && !showUpdatePassword ? (
                <form onSubmit={handleSubmit} className={styles.form}>
                    <TallPanel styles={styles.frameIn}></TallPanel>
                    <TallPanel styles={styles.frameOut}></TallPanel>
                    <img
                        alt={"logo"}
                        src={"/images/logo-screen.png"}
                        className={styles.logoImage}
                        style={{ maxWidth: "240px", width: "240px" }}
                    />

                    {/* Loader visible pendant l'initialisation */}
                    {!isInitialized ? (
                        <div className={styles.spinnerContainer}>
                            <div className={styles.spinner}></div>
                            <p className={styles.loadingText}>Chargement...</p>
                        </div>
                    ) : (
                        <>
                            <p>{error}</p>

                            <div className={styles.blocText}>
                                <svg width="280" height="40" viewBox="0 0 280 40" fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={styles.placeholder} preserveAspectRatio={"none"}>
                                    <path
                                        d="M3.09944e-06 27.9481C3.09944e-06 29.6991 0.916003 31.3225 2.41482 32.2279L14.0894 39.2798C14.8694 39.751 15.7633 40 16.6746 40H275C277.761 40 280 37.7614 280 35V13.1638C280 11.4058 279.077 9.77691 277.568 8.87386L263.933 0.710075C263.156 0.245407 262.269 0 261.364 0H5C2.23858 0 3.09944e-06 2.23858 3.09944e-06 5V27.9481Z"
                                        fill="#92AFAE" fill-opacity="0.2" />
                                </svg>
                                <input type="text" value={mail} onChange={handleChange} data-entry={"mail"} placeholder={"E-mail"}
                                    className={styles.input} />
                            </div>

                            <div className={styles.blocText}>
                                <svg width="280" height="40" viewBox="0 0 280 40" fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={styles.placeholder} preserveAspectRatio={"none"}>
                                    <path
                                        d="M3.09944e-06 27.9481C3.09944e-06 29.6991 0.916003 31.3225 2.41482 32.2279L14.0894 39.2798C14.8694 39.751 15.7633 40 16.6746 40H275C277.761 40 280 37.7614 280 35V13.1638C280 11.4058 279.077 9.77691 277.568 8.87386L263.933 0.710075C263.156 0.245407 262.269 0 261.364 0H5C2.23858 0 3.09944e-06 2.23858 3.09944e-06 5V27.9481Z"
                                        fill="#92AFAE" fill-opacity="0.2" />
                                </svg>
                                <input type="password" value={mdp} onChange={handleChange} data-entry={"mdp"}
                                    placeholder={"Password"} className={styles.input} />
                            </div>

                            <input type="submit" value="Submit" className={styles.submit} />

                            <p className={styles.poweredby}>Powered by </p>
                            <img className={styles.powered} stalt={"test"} src={"/images/logo.svg"}></img>
                            <a href="https://join.sav-iol.com" className={styles.registerLink} onClick={() => {
                                logEvent("User", "click", "register_link");
                            }}>Register Now</a>
                            <a href="#" className={styles.registerLink} onClick={(e) => {
                                e.preventDefault();
                                setShowResetPassword(true);
                                logEvent("User", "click", "forgot_password");
                            }}>Forgot your password ?</a>
                        </>
                    )}
                </form>
            ) : showResetPassword ? (
                <form onSubmit={handleResetPassword} className={styles.form}>
                    <TallPanel styles={styles.frameIn}></TallPanel>
                    <TallPanel styles={styles.frameOut}></TallPanel>
                    <img
                        alt={"logo"}
                        src={"/images/logo-screen.png"}
                        className={styles.logoImage}
                        style={{ maxWidth: "340px", width: "340px" }}
                    />
                    <h3>Reset Password</h3>
                    {resetSuccess ? (
                        <p className={styles.successMessage}>A password reset email has been sent. Please check your inbox..</p>
                    ) : (
                        <>
                            <p>{error}</p>
                            <div className={styles.blocText}>
                                <svg width="280" height="40" viewBox="0 0 280 40" fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={styles.placeholder} preserveAspectRatio={"none"}>
                                    <path
                                        d="M3.09944e-06 27.9481C3.09944e-06 29.6991 0.916003 31.3225 2.41482 32.2279L14.0894 39.2798C14.8694 39.751 15.7633 40 16.6746 40H275C277.761 40 280 37.7614 280 35V13.1638C280 11.4058 279.077 9.77691 277.568 8.87386L263.933 0.710075C263.156 0.245407 262.269 0 261.364 0H5C2.23858 0 3.09944e-06 2.23858 3.09944e-06 5V27.9481Z"
                                        fill="#92AFAE" fillOpacity="0.2" />
                                </svg>
                                <input type="text" value={mail} onChange={handleChange} data-entry={"mail"} placeholder={"E-mail"}
                                    className={styles.input} />
                            </div>
                            <input type="submit" value="Envoyer" className={styles.submit} />
                        </>
                    )}
                    <a href="#" className={styles.backToLogin} onClick={(e) => {
                        e.preventDefault();
                        setShowResetPassword(false);
                        setResetSuccess(false);
                        logEvent("User", "click", "back_to_login");
                    }}>Back to login page</a>
                </form>
            ) : (
                <form onSubmit={handleUpdatePassword} className={styles.form}>
                    <TallPanel styles={styles.frameIn}></TallPanel>
                    <TallPanel styles={styles.frameOut}></TallPanel>
                    <img
                        alt={"logo"}
                        src={"/images/logo-screen.png"}
                        className={styles.logoImage}
                        style={{ maxWidth: "240px", width: "240px" }}
                    />
                    <h3>Define a new password</h3>
                    <p>{error}</p>
                    <div className={styles.blocText}>
                        <svg width="280" height="40" viewBox="0 0 280 40" fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={styles.placeholder} preserveAspectRatio={"none"}>
                            <path
                                d="M3.09944e-06 27.9481C3.09944e-06 29.6991 0.916003 31.3225 2.41482 32.2279L14.0894 39.2798C14.8694 39.751 15.7633 40 16.6746 40H275C277.761 40 280 37.7614 280 35V13.1638C280 11.4058 279.077 9.77691 277.568 8.87386L263.933 0.710075C263.156 0.245407 262.269 0 261.364 0H5C2.23858 0 3.09944e-06 2.23858 3.09944e-06 5V27.9481Z"
                                fill="#92AFAE" fillOpacity="0.2" />
                        </svg>
                        <input type="password" value={newPassword} onChange={handleChange} data-entry={"newPassword"}
                            placeholder={"Nouveau mot de passe"} className={styles.input} />
                    </div>
                    <div className={styles.blocText}>
                        <svg width="280" height="40" viewBox="0 0 280 40" fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={styles.placeholder} preserveAspectRatio={"none"}>
                            <path
                                d="M3.09944e-06 27.9481C3.09944e-06 29.6991 0.916003 31.3225 2.41482 32.2279L14.0894 39.2798C14.8694 39.751 15.7633 40 16.6746 40H275C277.761 40 280 37.7614 280 35V13.1638C280 11.4058 279.077 9.77691 277.568 8.87386L263.933 0.710075C263.156 0.245407 262.269 0 261.364 0H5C2.23858 0 3.09944e-06 2.23858 3.09944e-06 5V27.9481Z"
                                fill="#92AFAE" fillOpacity="0.2" />
                        </svg>
                        <input type="password" value={confirmPassword} onChange={handleChange} data-entry={"confirmPassword"}
                            placeholder={"Confirmer le mot de passe"} className={styles.input} />
                    </div>
                    <input type="submit" value="Mettre à jour" className={styles.submit} />
                </form>
            )}

            {/* Disclaimer pour l'affichage sur desktop */}
            <div className={styles.disclaimer}>
                For a better experience, we recommend using a desktop computer.
            </div>
        </div>
    )
}